import i18n from './index';

// Helper function to ensure i18n is initialized before using it
const getLanguage = () => {
  return i18n.language || navigator.language || 'en-US';
};

/**
 * Format a date according to the current locale
 * @param date Date to format
 * @param options DateTimeFormat options
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString(getLanguage(), options);
};

/**
 * Format a number according to the current locale
 * @param num Number to format
 * @param options NumberFormat options
 * @returns Formatted number string
 */
export const formatNumber = (num: number, options?: Intl.NumberFormatOptions): string => {
  return num.toLocaleString(getLanguage(), options);
};

/**
 * Format a number in compact notation according to the current locale (e.g. 1.2M, 3.4K)
 * @param num Number to format (nullish values render as '-')
 * @param options NumberFormat options
 * @returns Formatted number string
 */
export const formatCompactNumber = (num: number | null | undefined, options?: Intl.NumberFormatOptions): string => {
  if (num === null || num === undefined) return '-';
  return num.toLocaleString(getLanguage(), { notation: 'compact', maximumFractionDigits: 1, ...options });
};

/**
 * Format a currency amount according to the current locale
 * @param amount Amount to format (nullish values render as '-')
 * @param currencyCode Currency code (e.g., USD, GBP)
 * @param options NumberFormat options
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number | null | undefined,
  currencyCode = 'USD',
  options?: Intl.NumberFormatOptions,
): string => {
  if (amount === null || amount === undefined) return '-';
  return amount.toLocaleString(getLanguage(), {
    style: 'currency',
    currency: currencyCode,
    ...options,
  });
};